import type {TranslationFunction} from 'src/types/language-support';
import type {
  VoicebotState,
  VoicebotAPI,
  DynamicValidations,
  APIVoicebotPromptVariablesData,
  APIErrorResponseBody,
} from 'src/types/voicebot-v2';
import {
  parseNodeSchedule,
  normalizeNodeSchedule,
} from 'src/utils/node-schedule';
import {ApiError} from 'src/utils/errors';


export const ConfigPageType = Object.freeze({
  SELECT_VOICEBOT: 'SELECT_VOICEBOT',
  VOICEBOT_CONFIG: 'VOICEBOT_CONFIG',
});

/**
 * Converts comma separated prompt variables to object
 * @param {*} vars eg: 'ae_job/title,ae_job/state,ae_candidate/full_name,ae_candidate/first_name,ae_job/city'
 * @returns object { 'ae_job/title': null, 'ae_job/state': null, ...}
 */
export const transformPromptVariables = (
  vars: string | null,
): VoicebotState['prompt_variables'] => {
  const result: Record<string, any> = {};
  if (vars === null || vars === '') {
    return result;
  }

  // Split the input string by commas to get an array of keys
  const keys = vars.split(',');

  keys.forEach((key) => {
    result[key] = null;
  });

  return result;
};

// Changes field names of API response
export const transformStaticVariables = (
  vars: Array<{
    key: string;
    attribute: string;
    display_name: string;
  }>,
): APIVoicebotPromptVariablesData['static_variables'] => {
  return vars.map((variable) => ({
    name: variable.key,
    value: variable.display_name,
  }));
};

export const parseVoicebot = (data: VoicebotAPI): VoicebotState => {
  const voicebotParams = data.task.params;
  const {retry_1, retry_2, retry_3} = data;

  if (!voicebotParams) {
    throw new Error('Voiceflow params not found');
  }

  const {
    retell_bot_id,
    retell_agent_id,
    retell_bot_name,
    retell_bot_description,
    retell_llm_id,
    consent_type,
    max_concurrency_limit,
    service_provider,
    entity_type,
    from_number,
    to_number,
    recipient_name,
    prompt_variables,
    communication_method,
    secondary_communication_method,
    whatsapp_template_id,
    whatsapp_template_system_variables,
    whatsapp_template_user_mappings,
    email_sender_name,
    email_sender_address,
    email_receiver_address,
    email_subject,
    email_cc,
    email_bcc,
    email_template_system_variables,
    email_template_user_mappings,
    sms_body,
    content_subscription_category_id,
  } = voicebotParams;
  const nodeSchedule = parseNodeSchedule(data);

  return {
    retell_bot_id,
    retell_agent_id,
    retell_bot_name,
    retell_bot_description,
    retell_llm_id,
    consent_type,
    max_concurrency_limit,
    service_provider,
    entity_type,
    from_number,
    to_number,
    recipient_name,
    prompt_variables,
    retry_1,
    retry_2,
    retry_3,
    communication_method,
    secondary_communication_method,
    whatsapp_template_id,
    whatsapp_template_system_variables,
    whatsapp_template_user_mappings,
    email_sender_name,
    email_sender_address,
    email_receiver_address,
    email_subject,
    email_cc,
    email_bcc,
    email_template_system_variables,
    email_template_user_mappings,
    sms_body: sms_body?.replace('<cta>', '{{cta}}') ?? sms_body,
    content_subscription_category_id,
    ...nodeSchedule,
  };
};

export const normalizeVoicebot = (
  voicebotState: VoicebotState,
): VoicebotAPI => {
  const {
    retell_bot_id,
    retell_agent_id,
    retell_bot_name,
    retell_bot_description,
    retell_llm_id,
    consent_type,
    max_concurrency_limit,
    service_provider,
    entity_type,
    from_number,
    to_number,
    recipient_name,
    prompt_variables,
    retry_1,
    retry_2,
    retry_3,
    communication_method,
    secondary_communication_method,
    whatsapp_template_id,
    whatsapp_template_system_variables,
    whatsapp_template_user_mappings,
    email_sender_name,
    email_sender_address,
    email_receiver_address,
    email_subject,
    email_cc,
    email_bcc,
    email_template_system_variables,
    email_template_user_mappings,
    sms_body,
    content_subscription_category_id,
  } = voicebotState;
  const nodeSchedule = normalizeNodeSchedule(voicebotState);

  return {
    task: {
      params: {
        retell_bot_id,
        retell_agent_id,
        retell_bot_name,
        retell_bot_description,
        retell_llm_id,
        consent_type,
        max_concurrency_limit,
        service_provider,
        entity_type,
        from_number,
        // for email communication, phone number needs to be auto populated with receiver email
        to_number:
          communication_method === 'email' ? email_receiver_address : to_number,
        recipient_name,
        prompt_variables,
        communication_method,
        secondary_communication_method,
        whatsapp_template_id,
        whatsapp_template_system_variables,
        whatsapp_template_user_mappings,
        email_sender_name,
        email_sender_address,
        email_receiver_address,
        email_subject,
        email_cc,
        email_bcc,
        email_template_system_variables,
        email_template_user_mappings,
        sms_body: sms_body?.replace('{{cta}}', '<cta>') ?? sms_body,
        content_subscription_category_id,
      },
    },
    retry_1,
    retry_2,
    retry_3,
    ...nodeSchedule,
  };
};

export const getErrors = (
  validations: DynamicValidations,
  retryErrors: {
    invalidInput: boolean;
    exceedValidDuration: boolean;
  },
  maxRetry: number,
  t: TranslationFunction,
): {errors: Array<string>; tabsError: Record<string, boolean>} => {
  const errors: Array<string> = [];
  const tabsError: Record<string, boolean> = {
    voiceflow: false,
    retries: false,
    settings: false,
  };
  const {
    callToNumberEmpty,
    callToNumberInvalid,
    recipientNameEmpty,
    ...restValidations
  } = validations || {};

  const whatsappVars = Object.keys(restValidations).filter((key) =>
    key.startsWith('whatsapp_'),
  );

  const emailVars = Object.keys(restValidations).filter((key) =>
    key.startsWith('email_'),
  );

  const voiceflowVars = Object.keys(restValidations).filter((key) =>
    key.startsWith('voiceflow_'),
  );

  const smsBodyEmpty = restValidations.sms_body_empty;
  const smsBodyInvalid = restValidations.sms_body_invalid;

  const anyVoiceflowEmpty = voiceflowVars
    .filter((key) => key.endsWith('_empty'))
    .some((key) => Boolean(restValidations[key]));

  const anyVoiceflowInvalid = voiceflowVars
    .filter((key) => key.endsWith('_invalid'))
    .some((key) => Boolean(restValidations[key]));

  const anyWhatsappEmpty = whatsappVars
    .filter((key) => key.endsWith('_empty'))
    .some((key) => Boolean(restValidations[key]));

  const anyEmailEmpty = emailVars
    .filter((key) => key.endsWith('_empty'))
    .some((key) => Boolean(restValidations[key]));

  const recieverAddressInvalid = emailVars
    .filter((key) => key.endsWith('_invalid'))
    .some((key) => Boolean(restValidations[key]));

  const anySettingsEmpty =
    Boolean(callToNumberEmpty) ||
    Boolean(recipientNameEmpty) ||
    anyWhatsappEmpty ||
    anyEmailEmpty ||
    smsBodyEmpty;

  if (anyVoiceflowEmpty || anySettingsEmpty) {
    if (anyVoiceflowEmpty && anySettingsEmpty) {
      errors.push(
        t(
          'Please_fill_the_required_fields_in_Voiceflow_and_Settings',
          'Please fill the required fields in Voiceflow and Settings.',
        ),
      );
      tabsError['voiceflow'] = true;
      tabsError['settings'] = true;
    } else if (anyVoiceflowEmpty) {
      errors.push(
        t(
          'Please_fill_the_required_fields_in_Voiceflow',
          'Please fill the required fields in Voiceflow.',
        ),
      );
      tabsError['voiceflow'] = true;
    } else {
      errors.push(
        t(
          'Please_fill_the_required_fields_in_Settings',
          'Please fill the required fields in Settings.',
        ),
      );
      tabsError['settings'] = true;
    }
  }
  if (anyVoiceflowInvalid) {
    errors.push(
      t(
        'Please_enter_valid_variables_in_Voiceflow',
        'Please enter valid variables in Voiceflow.',
      ),
    );
    tabsError['voiceflow'] = true;
  }
  if (callToNumberInvalid) {
    errors.push(
      `${t(
        'Please_choose_a_valid_receiver_phone_number_from_the_variable_picker_in_Settings',
        'Please choose a valid receiver phone number from the variable picker in Settings',
      )} — ${t('manual_entry_is_not_allowed', 'manual entry is not allowed')}.`,
    );
    tabsError['settings'] = true;
  }
  if (smsBodyInvalid) {
    errors.push(
      `${t(
        'Please_add_call_link_in_SMS_body_inside_settings',
        'Please add call link in SMS body inside settings',
      )}.`,
    );
    tabsError['settings'] = true;
  }
  if (recieverAddressInvalid) {
    errors.push(
      `${t(
        'Select_Receiver_Email_from_the_dropdown_in_Settings',
        'Select Receiver Email from the dropdown in Settings',
      )}`,
    );
    tabsError['settings'] = true;
  }
  if (retryErrors.invalidInput || retryErrors.exceedValidDuration) {
    tabsError['retries'] = true;
    if (retryErrors.invalidInput) {
      errors.push(
        `${t(
          'Retries_have_invalid_inputs',
          'Retries have invalid inputs',
        )}. ${t(
          'Please_enter_valid_retry_durations',
          'Please enter valid retry durations',
        )}.`,
      );
    }
    if (retryErrors.exceedValidDuration) {
      errors.push(
        `${t(
          'Total_duration_cannot_be_more_than',
          'Total duration cannot be more than',
        )} ${maxRetry} ${
          maxRetry > 1 ? t('days', 'days') : t('day', 'day')
        } ${t('for_retries', 'for retries')}.`,
      );
    }
  }
  return {errors, tabsError};
};

export const getRHFErrors = (
  formErrors: any,
  t: TranslationFunction,
): {errors: Array<string>; tabsError: Record<string, boolean>} => {
  const errors: any = [];
  const tabsError: any = {};
  const retryKeys = ['retry'];
  const voiceflowKeys = ['prompt_variables'];
  const settingsKeys = [
    'to_number',
    'recipient_name',
    'email_sender_name',
    'email_sender_address',
    'email_receiver_address',
    'email_subject',
    'email_cc',
    'email_bcc',
    'email_template_system_variables',
    'email_template_user_mappings',
    'whatsapp_template_id',
    'whatsapp_template_user_mappings',
    'sms_body',
  ];
  if (Object.keys(formErrors).some((key) => voiceflowKeys.includes(key))) {
    tabsError['voiceflow'] = true;
    errors.push(
      t(
        'Please_check_Voiceflow_fields_for_missing_or_invalid_values',
        'Please check Voiceflow fields for missing or invalid values.',
      ),
    );
  }
  if (Object.keys(formErrors).some((key) => retryKeys.includes(key))) {
    tabsError['retries'] = true;
    if (formErrors.retry?.retries) {
      errors.push(formErrors.retry?.retries?.message);
    } else {
      errors.push(
        t(
          'Please_enter_valid_retry_durations',
          'Please enter valid retry durations.',
        ),
      );
    }
  }
  if (Object.keys(formErrors).some((key) => settingsKeys.includes(key))) {
    tabsError['settings'] = true;
    errors.push(
      t(
        'Please_complete_all_required_fields_or_correct_invalid_entries_in_Settings',
        'Please complete all required fields or correct invalid entries in Settings.',
      ),
    );
  }
  return {errors, tabsError};
};

export const getEmailData = (data: Partial<VoicebotState>) => {
  return {
    email_sender_name: data.email_sender_name,
    email_sender_address: data.email_sender_address,
    email_receiver_address: data.email_receiver_address,
    email_subject: data.email_subject,
    email_cc: data.email_cc ?? null,
    email_bcc: data.email_bcc ?? null,
    email_template_system_variables: data.email_template_system_variables ?? [],
    email_template_user_mappings: data.email_template_user_mappings ?? {},
  };
};

/**
 * reason will be of this type 'dial_no_answer'
 * returns 'dial no answer'
 */
export const formatFailureReason = (reason: unknown): string => {
  if (!reason || typeof reason !== 'string') {
    return '';
  }
  return reason.split('_').join(' ');
};

/**
 * Populates target object with values from source object where keys match
 * @param {Object} to - Target object to be populated
 * @param {Object} from - Source object containing values to populate from
 * @returns {Object} Updated target object
 */
export const populatePromptVariables = (
  to: Record<string, string>,
  from: Record<string, string>,
): Record<string, string> => {
  const result = {...to} as const;
  Object.keys(result).forEach((key) => {
    if (key in from) {
      // @ts-expect-error - TS2542 - Index signature in type '{ readonly [key: string]: string | null | undefined; }' only permits reading.
      result[key] = from[key];
    }
  });

  return result;
};

export const getUnitAndValue = (delay: VoicebotState['retry_1']['delay']) => {
  const numberOfDays = delay?.date_offset?.days;
  const unit = numberOfDays != null ? 'days' : 'hours';
  const value = numberOfDays ? numberOfDays : delay?.time_offset?.hours ?? 0;
  return {unit, value};
};

export const getBackendErrorBody = (
  error: Error | null | undefined,
): APIErrorResponseBody | undefined => {
  if (!error) {
    return undefined;
  }
  if (error instanceof ApiError && error.responseBody != null) {
    const body: any = error.responseBody;
    return body;
  }
  return {
    message: error?.message || '',
  };
};

export const normalizeVoicebotDataToRHF = (data: VoicebotState) => {
  return {
    ...data,
    // strings
    retell_bot_id: data.retell_bot_id ?? '',
    retell_agent_id: data.retell_agent_id ?? '',
    retell_bot_name: data.retell_bot_name ?? '',
    retell_bot_description: data.retell_bot_description ?? '',
    retell_llm_id: data.retell_llm_id ?? '',
    consent_type: data.consent_type ?? '',
    max_concurrency_limit: data.max_concurrency_limit ?? '',
    service_provider: data.service_provider ?? '',
    entity_type: data.entity_type ?? '',
    from_number: data.from_number ?? '',
    to_number: data.to_number ?? '',
    recipient_name: data.recipient_name ?? '',
    secondary_communication_method: data.secondary_communication_method ?? null,

    // email
    email_sender_name: data.email_sender_name ?? '',
    email_sender_address: data.email_sender_address ?? '',
    email_receiver_address: data.email_receiver_address ?? '',
    email_subject: data.email_subject ?? '',
    email_cc: data.email_cc ?? [],
    email_bcc: data.email_bcc ?? [],
    email_template_system_variables: data.email_template_system_variables ?? [],
    email_template_user_mappings: data.email_template_user_mappings ?? {},

    // whatsapp
    whatsapp_template_id: data.whatsapp_template_id ?? '',
    whatsapp_template_user_mappings: data.whatsapp_template_user_mappings ?? {},
    whatsapp_template_system_variables:
      data.whatsapp_template_system_variables ?? [],

    // sms
    sms_body: data.sms_body ?? '',
  };
};
